import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import PropTypes from "prop-types";
import SectionHeader from "./SectionHeader";
import FieldLabel from "./FieldLabel";
import { ButtonV1, MultiSelectSearchDropdown } from "components";

const inputSx = {
  borderRadius: 1,
  "& .MuiOutlinedInput-input": {
    py: "10px",
    px: 2,
    fontSize: 13,
  },
};

const DispatchOrders = ({
  orderNumber = "",
  onOrderNumberChange,
  onSearchOrder,
  isSearching = false,
  availablePsIds = [],
  selectedPsIds = [],
  onSelectedPsIdsChange,
  dispatchOrders = [],
  onUpdateLineItem,
}) => {
  const theme = useTheme();

  return (
    <Box mb={3}>
      <Box sx={{ display: "flex" }}>
        <SectionHeader stepNumber={1} title="Dispatch Orders" />
        <FieldLabel title="" />
      </Box>
      <Box
        sx={{
          border: `1px solid ${theme.palette.grey["400"]}`,
          borderRadius: 2,
          padding: 3,
          mb: 3,
        }}
      >
        <Box
          sx={{
            mb: 3,
            p: 2,
            backgroundColor: theme.palette.grey["100"],
            borderRadius: 1.5,
          }}
        >
          <Typography
            sx={{
              fontSize: 13,
              fontWeight: 600,
              mb: 1,
              color: theme.palette.text.primary,
            }}
          >
            Search Order Number
          </Typography>
          <Box
            sx={{
              display: "flex",
              gap: 1.5,
              alignItems: "center",
              maxWidth: 500,
            }}
          >
            <TextField
              fullWidth
              size="small"
              value={orderNumber}
              onChange={(e) => onOrderNumberChange?.(e.target.value)}
              placeholder="Enter Order Number (e.g. JOO-02V431GZL)"
              disabled={isSearching}
              sx={inputSx}
            />
            <ButtonV1
              title={"Search"}
              variant="contained"
              color="primary"
              onClick={onSearchOrder}
              disabled={isSearching || !orderNumber?.trim()}
              style={{
                minWidth: 100,
                height: 40,
                fontSize: 13,
                fontWeight: 600,
                textTransform: "none",
              }}
            />
          </Box>

          {availablePsIds.length > 0 && (
            <Box
              sx={{
                mt: 2,
                pt: 2,
                borderTop: `1px solid ${theme.palette.grey["300"]}`,
              }}
            >
              <Typography
                sx={{
                  fontSize: 13,
                  fontWeight: 600,
                  mb: 2,
                  color: theme.palette.text.primary,
                }}
              >
                Select PS ID(s)
              </Typography>
              <Box sx={{ maxWidth: 500 }}>
                <MultiSelectSearchDropdown
                  value={selectedPsIds}
                  onChange={onSelectedPsIdsChange}
                  label="PS IDs"
                  selectAll
                  options={availablePsIds}
                />
              </Box>
            </Box>
          )}
        </Box>

        {availablePsIds.length === 0 && dispatchOrders.length === 0 && (
          <Box
            sx={{
              p: 3,
              textAlign: "center",
              backgroundColor: theme.palette.grey["50"],
              borderRadius: 1.5,
              border: `1px dashed ${theme.palette.grey["300"]}`,
            }}
          >
            <Typography
              sx={{ fontSize: 13, color: theme.palette.text.secondary }}
            >
              Enter an Order Number above and search to view available PS IDs
              and Dispatch Orders.
            </Typography>
          </Box>
        )}

        {availablePsIds.length > 0 && dispatchOrders.length === 0 && (
          <Box
            sx={{
              p: 3,
              textAlign: "center",
              backgroundColor: theme.palette.grey["50"],
              borderRadius: 1.5,
              border: `1px dashed ${theme.palette.grey["300"]}`,
            }}
          >
            <Typography
              sx={{ fontSize: 13, color: theme.palette.text.secondary }}
            >
              Please select one or more PS IDs from the dropdown above to view
              and update dispatch orders.
            </Typography>
          </Box>
        )}

        {dispatchOrders.map((order, doIdx) => (
          <Box key={`do-${order.doId || doIdx}`} mb={2.5}>
            <Box
              sx={{
                border: `1px solid ${theme.palette.grey["400"]}`,
                borderRadius: "8px 8px 0 0",
                px: 2,
                py: 1.25,
                display: "flex",
                alignItems: "center",
                backgroundColor: theme.palette.grey["50"],
              }}
            >
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: 14,
                }}
              >
                Dispatch Order {doIdx + 1} {order.doId ? `(${order.doId})` : ""}
              </Typography>
            </Box>

            <Box
              sx={{
                p: 2,
                border: `1px solid ${theme.palette.grey["400"]}`,
                borderTop: "none",
                borderRadius: "0 0 8px 8px",
              }}
            >
              <Box mb={2} maxWidth={320}>
                <Typography
                  sx={{
                    fontSize: 13,
                    fontWeight: 600,
                    mb: 0.5,
                    ml: 1,
                  }}
                >
                  DO ID{" "}
                  <span style={{ color: theme.palette.error.dark }}>*</span>
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  value={order.doId || ""}
                  disabled
                  placeholder="e.g. PS-K19JB"
                />
              </Box>

              <Typography
                sx={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: theme.palette.text.secondary,
                  mb: 1,
                }}
              >
                SKU Line Items
              </Typography>

              <TableContainer
                component={Paper}
                elevation={0}
                sx={{
                  border: `1px solid ${theme.palette.grey["400"]}`,
                  borderRadius: 1,
                }}
              >
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{
                          width: 50,
                          fontWeight: 600,
                          fontSize: 12,
                          textAlign: "center",
                          py: 1,
                        }}
                      >
                        S.No
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 600,
                          fontSize: 12,
                          py: 1,
                        }}
                      >
                        Material Master ID{" "}
                        <span style={{ color: theme.palette.error.dark }}>
                          *
                        </span>
                      </TableCell>
                      <TableCell
                        sx={{
                          width: 220,
                          fontWeight: 600,
                          fontSize: 12,
                          py: 1,
                        }}
                      >
                        Quantity{" "}
                        <span style={{ color: theme.palette.error.dark }}>
                          *
                        </span>
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 600,
                          fontSize: 12,
                          py: 1,
                        }}
                      >
                        Line Item Number
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(order.lineItems || []).map((item, itemIdx) => {
                      const hasExceeded = Boolean(
                        item.quantity &&
                          item.maxAllowedQty &&
                          Number(item.quantity) > Number(item.maxAllowedQty),
                      );

                      return (
                        <TableRow key={`item-${itemIdx}`} hover>
                          <TableCell
                            sx={{
                              textAlign: "center",
                              fontSize: 13,
                              py: 3,
                              verticalAlign: "top",
                            }}
                          >
                            {itemIdx + 1}
                          </TableCell>
                          <TableCell sx={{ py: 2, verticalAlign: "top" }}>
                            <TextField
                              fullWidth
                              size="small"
                              value={item.materialMasterId || ""}
                              disabled
                            />
                          </TableCell>
                          <TableCell sx={{ py: 2, verticalAlign: "top" }}>
                            <TextField
                              fullWidth
                              size="small"
                              value={item.quantity || ""}
                              onChange={(e) =>
                                onUpdateLineItem?.(
                                  doIdx,
                                  itemIdx,
                                  "quantity",
                                  e.target.value,
                                )
                              }
                              placeholder={
                                item.maxAllowedQty
                                  ? `Max: ${item.maxAllowedQty}`
                                  : "e.g. 2.0"
                              }
                              error={hasExceeded}
                              helperText={
                                hasExceeded
                                  ? `Exceeding allowed qty: max is ${item.maxAllowedQty}`
                                  : item.maxAllowedQty !== undefined
                                  ? `Allowed: ${item.maxAllowedQty} ${
                                      item.uom || ""
                                    }`.trim()
                                  : ""
                              }
                            />
                          </TableCell>
                          <TableCell sx={{ py: 2, verticalAlign: "top" }}>
                            <TextField
                              fullWidth
                              size="small"
                              value={item.lineItemNumber || ""}
                              disabled
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

DispatchOrders.propTypes = {
  orderNumber: PropTypes.string,
  onOrderNumberChange: PropTypes.func,
  onSearchOrder: PropTypes.func,
  isSearching: PropTypes.bool,
  availablePsIds: PropTypes.array,
  selectedPsIds: PropTypes.array,
  onSelectedPsIdsChange: PropTypes.func,
  dispatchOrders: PropTypes.array.isRequired,
  onUpdateLineItem: PropTypes.func,
};

export default DispatchOrders;
